import { TopBar } from "@/components/layout/TopBar";
import { useI18n } from "@/lib/i18n";
import { ShieldCheck, FileText, KanbanSquare, CalendarDays, Radar, Compass, Plug, GitBranch, Network, Brain, Globe2, Map, Gauge } from "lucide-react";

function ModulePreview({ title, features, icon: Icon }) {
  return (
    <div className="px-6 md:px-10 py-8">
      <div className="max-w-3xl">
        <div className="inline-flex items-center gap-2 rounded-md border border-[#0B2545]/25 bg-[#0B2545]/8 px-3 py-1">
          <Icon className="h-4 w-4 text-[#0B2545]" strokeWidth={1.5} />
          <span className="font-mono text-[10px] uppercase tracking-[0.24em] text-[#0B2545]">Module planifié</span>
        </div>
        <h2 className="mt-6 font-display text-2xl md:text-3xl tracking-tight text-[#1F2937]">{title}</h2>
        <p className="mt-3 text-sm text-[#5B5B62] leading-relaxed">
          L'architecture est prête et documentée. L'activation est pilotée par un feature flag depuis la Console Fondateur.
        </p>
        <div className="mt-8 grid grid-cols-1 sm:grid-cols-2 gap-3">
          {features.map((f) => (
            <div key={f} className="rounded-md border border-[#E4E1D8] bg-white px-4 py-3">
              <div className="text-sm text-[#1F2937]">{f}</div>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}

export function Vault() {
  const { t } = useI18n();
  return (
    <div>
      <TopBar title="ALTIORA Vault" subtitle="Coffre-fort documentaire chiffré" />
      <ModulePreview
        icon={ShieldCheck}
        title={t("nav.vault")}
        features={[
          "Stockage AES-256 + object storage chiffré",
          "OCR automatique (CNI, diplômes, certifications)",
          "Alertes d'expiration (permis, certifications)",
          "Versioning des documents (CV, portfolio)",
          "Journal d'audit détaillé (Zero-Trust)",
          "Utilisation automatique lors des candidatures",
        ]}
      />
    </div>
  );
}

export function Documents() {
  const { t } = useI18n();
  return (
    <div>
      <TopBar title={t("nav.documents")} subtitle="Générateur documentaire IA multilingue" />
      <ModulePreview
        icon={FileText}
        title={t("nav.documents")}
        features={[
          "CV ciblé (GPT-5.4) adapté à l'offre & langue de l'annonce",
          "Lettre de motivation par entreprise & rôle",
          "Courriel de candidature (ton, longueur, contexte)",
          "Fiche de préparation d'entrevue (méthode STAR)",
          "Réponses aux questions fréquentes (personnalisées)",
          "Messages de réseautage (LinkedIn, événement, cold intro)",
          "Profil LinkedIn optimisé (headline, résumé, expériences)",
          "Historique de versions + comparateur",
        ]}
      />
    </div>
  );
}

export function Tracker() {
  const { t } = useI18n();
  return (
    <div>
      <TopBar title={t("nav.tracker")} subtitle="Kanban de suivi des candidatures" />
      <ModulePreview
        icon={KanbanSquare}
        title={t("nav.tracker")}
        features={[
          "Colonnes Kanban: Prospection · Postulé · Entrevue · Offre · Décision",
          "Rappels et échéances par candidature",
          "Pièces jointes liées à ALTIORA Vault",
          "Analytics: taux de réponse, temps moyen par étape",
        ]}
      />
    </div>
  );
}

export function CalendarPage() {
  const { t } = useI18n();
  return (
    <div>
      <TopBar title={t("nav.calendar")} subtitle="Événements, certifications et réseautage" />
      <ModulePreview
        icon={CalendarDays}
        title={t("nav.calendar")}
        features={[
          "Événements pro (salons, meetups, webinaires)",
          "Rappels de certifications & renouvellements",
          "Synchronisation Google Calendar / iCal",
          "Suggestions de réseautage pilotées par IA",
        ]}
      />
    </div>
  );
}

export function HiddenOpportunityEngine() {
  return (
    <div>
      <TopBar title="Hidden Opportunity Engine™" subtitle="Détection multi-source : opportunités visibles ET invisibles" />
      <ModulePreview
        icon={Radar}
        title="Hidden Opportunity Engine™"
        features={[
          "Offres d'emploi visibles (via Connector Engine)",
          "Entreprises recrutant sans publication (funding, exec moves)",
          "Organismes d'employabilité (référents & accompagnement)",
          "Chambres de commerce & associations professionnelles",
          "Incubateurs & accélérateurs",
          "Événements de réseautage pertinents",
          "Formations & certifications à fort ROI",
          "Programmes publics & subventions (ATE, PATP, Emploi-Québec…)",
          "Recommandations IA personnalisées (via Learning Memory Engine™)",
        ]}
      />
    </div>
  );
}

export function DecisionEngine() {
  return (
    <div>
      <TopBar title="ALTIORA Decision Engine™" subtitle="Le cerveau de la plateforme — action prioritaire & meilleur ROI" />
      <ModulePreview
        icon={Compass}
        title="ALTIORA Decision Engine™"
        features={[
          "Que dois-je faire aujourd'hui ? (1–3 actions max)",
          "Que dois-je faire cette semaine ? (cadence coordonnée)",
          "Quelle est l'action prioritaire maintenant ?",
          "Quelle action offre le meilleur ROI (temps × coût × salaire × probabilité) ?",
          "Fusion de 8 collecteurs de signaux (profil, score, offres, hidden, formations, événements, docs)",
          "Coach narrative IA (Claude) — message court, direct, actionnable",
          "Apprend du Learning Memory Engine™ pour affiner ses recommandations",
        ]}
      />
    </div>
  );
}

export function ConnectorEngine() {
  return (
    <div>
      <TopBar title="Connector Engine" subtitle="Couche indépendante d'adaptateurs externes" />
      <ModulePreview
        icon={Plug}
        title="Connector Engine"
        features={[
          "Emploi : LinkedIn, Indeed, France Travail, Emploi-Québec, Welcome",
          "Formations : Coursera, LinkedIn Learning, OpenClassrooms",
          "Calendriers : Google Calendar, Microsoft 365, iCal",
          "Événements : Meetup, Eventbrite",
          "Gouvernement : Service Canada, Pôle Emploi API",
          "CRM entreprise : Salesforce, HubSpot",
          "Partenaires : SDK connecteur générique (40 lignes)",
        ]}
      />
    </div>
  );
}

export function CareerSimulation() {
  return (
    <div>
      <TopBar title="Career Simulation Engine™" subtitle="Comparer des scénarios de carrière : délai, coût, revenu, ROI, probabilité, risques" />
      <ModulePreview
        icon={GitBranch}
        title="Career Simulation Engine™"
        features={[
          "Scénario certification (ex : AWS Solutions Architect)",
          "Scénario relocalisation (ex : Montréal → Toronto)",
          "Scénario DEP / formation technique",
          "Scénario retour à l'université (maîtrise, MBA)",
          "Scénario changement de rôle (IC → Manager)",
          "Scénario transition freelance / consulting",
          "Matrice de comparaison côte-à-côte + risques classés",
        ]}
      />
    </div>
  );
}

export function KnowledgeGraph() {
  return (
    <div>
      <TopBar title="Career Knowledge Graph" subtitle="Graphe reliant métiers, compétences, certifications, employeurs, formations, organismes, événements, réseaux" />
      <ModulePreview
        icon={Network}
        title="Career Knowledge Graph"
        features={[
          "11 types de nœuds (role, skill, certification, employer, formation, organisation, event, public_program, location…)",
          "14 types d'arêtes (REQUIRES, BOOSTED_BY, TAUGHT_BY, ISSUED_BY, HIRES, REFERRERS, FUNDS…)",
          "Alimente : opportunity, hidden_opps, simulation, decision, memory",
          "Path finding : user → referrer → employer (2-3 sauts)",
          "Résolution de skills-gap et déblocage de certifications",
          "Embeddings de nœuds pour recommandations vectorielles",
        ]}
      />
    </div>
  );
}

export function LearningMemory() {
  return (
    <div>
      <TopBar title="Learning Memory Engine™" subtitle="L'IA apprend de chaque utilisateur — sans jamais lui demander de tout ressaisir" />
      <ModulePreview
        icon={Brain}
        title="Learning Memory Engine™"
        features={[
          "Capte : métiers refusés, candidatures retenues, entretiens réussis",
          "Capte : préférences, compétences acquises, objectifs qui évoluent",
          "Filtre intelligent : plus jamais de suggestion refusée",
          "Boost : offres similaires à celles que vous avez sauvegardées",
          "Adaptation du ton / structure des documents générés",
          "Vecteur de préférences décrement dans le temps (récence)",
          "Consommé par : opportunity, hidden_opps, decision, generator",
        ]}
      />
    </div>
  );
}

export function GlobalMobility() {
  return (
    <div>
      <TopBar
        title="Global Mobility & Trajectory Engine™"
        subtitle="Trajectoire professionnelle & mobilité internationale — optimisée par l'IA"
      />
      <div className="px-6 md:px-10 py-8 space-y-8">
        {/* 3 sub-components */}
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
          <div className="rounded-md border border-[#0B2545]/25 bg-gradient-to-br from-[#EBE8DE] to-[#FFFFFF] p-5">
            <Globe2 className="h-5 w-5 text-[#0B2545]" strokeWidth={1.5} />
            <div className="mt-3 text-sm font-medium text-[#1F2937]">Trajectory Planner</div>
            <p className="mt-1 text-xs text-[#5B5B62] leading-relaxed">10 types de trajectoires : certification, reconnaissance de diplôme, expérience locale, langue, DEP, changement de province, changement de pays, études, employeur, programme gouvernemental.</p>
          </div>
          <div className="rounded-md border border-[#0B2545]/25 bg-gradient-to-br from-[#EBE8DE] to-[#FFFFFF] p-5">
            <Map className="h-5 w-5 text-[#0B2545]" strokeWidth={1.5} />
            <div className="mt-3 text-sm font-medium text-[#1F2937]">Global Opportunity Map™</div>
            <p className="mt-1 text-xs text-[#5B5B62] leading-relaxed">Par pays/région : métiers compatibles, compétences manquantes, certifications, formations, employeurs, événements, organismes, opportunités cachées, salaires, actions.</p>
          </div>
          <div className="rounded-md border border-[#0B2545]/25 bg-gradient-to-br from-[#EBE8DE] to-[#FFFFFF] p-5">
            <Gauge className="h-5 w-5 text-[#0B2545]" strokeWidth={1.5} />
            <div className="mt-3 text-sm font-medium text-[#1F2937]">Immigration Readiness Index™</div>
            <p className="mt-1 text-xs text-[#5B5B62] leading-relaxed">Mesure la préparation du profil selon les critères analysés par ALTIORA. <strong className="text-[#1F2937]">Ne prédit ni ne garantit aucune décision officielle.</strong></p>
          </div>
        </div>

        <ModulePreview
          icon={Globe2}
          title="Signaux d'entrée analysés"
          features={[
            "Âge, niveau d'études, diplômes, expériences",
            "Compétences, certifications, langues",
            "Situation familiale & statut d'immigration actuel",
            "Pays d'origine & pays / régions ciblés",
            "Ressources financières & mobilité géographique",
            "Objectifs de revenus & délais souhaités",
          ]}
        />

        <ModulePreview
          icon={Compass}
          title="Questions auxquelles le moteur répond"
          features={[
            "Quelle est la meilleure trajectoire pour atteindre mon objectif ?",
            "Quel pays correspond le mieux à mon profil ?",
            "Quelle province ou région offre les meilleures opportunités ?",
            "Quelles compétences dois-je acquérir ?",
            "Quelle certification aura le plus d'impact ?",
            "Quelle est la prochaine meilleure action à entreprendre ?",
          ]}
        />

        {/* Legal disclaimer — always visible */}
        <div className="rounded-md border border-[#D4AF37]/40 bg-[#D4AF37]/5 p-5">
          <div className="text-[10px] font-mono uppercase tracking-[0.24em] text-[#D4AF37] mb-2">
            Avertissement légal · Immigration Readiness Index™
          </div>
          <p className="text-xs text-[#5B5B62] leading-relaxed">
            L'Immigration Readiness Index™ <strong className="text-[#1F2937]">ne prédit et ne garantit AUCUNE décision officielle d'immigration</strong>. Il mesure uniquement le niveau de préparation du profil par rapport aux critères analysés par ALTIORA et suggère des actions susceptibles d'améliorer les chances de réussite du projet de mobilité internationale. Consultez un professionnel autorisé pour toute démarche d'immigration.
          </p>
        </div>
      </div>
    </div>
  );
}
